import React, {useEffect, useMemo, useState} from 'react';
import {Box, FormControlLabel, Stack, Switch, Typography} from '@mui/material';
import {useQuery} from '@apollo/client/react';
import {Loader} from 'components/common/General';
import {useDialogContext} from 'components/dialog/DialogContext';
import {useRecordContext} from 'components/record/useRecordContext';
import {useFetch, useMutate} from 'hooks/useFetchData';
import QUERIES from 'services/gqlQueries';
import {toast} from 'react-toastify';
import {RecordType} from 'components/record/RecordContext';

export const ROLE_PERMISSIONS_FORM_ID = 'role_permissions_form';

type PermissionResource = {
  resource: string;
  description: string;
  actions: Array<{
    action: string;
    description: string;
    key: string;
    permissionId?: number;
  }>;
};

type RolePermissionsFormProps = {
  onSuccess?: () => void;
  readOnly?: boolean;
};

const RolePermissionsForm = ({onSuccess, readOnly = false}: RolePermissionsFormProps) => {
  const {id} = useRecordContext();
  const {close} = useDialogContext();
  const [grantedByPermissionId, setGrantedByPermissionId] = useState<Record<number, boolean>>({});

  const {data: resourcesData, loading: resourcesLoading} = useQuery<{getPermissionResources: PermissionResource[]}>(
    QUERIES.getPermissionResources
  );
  const {data: roleData, loading: roleLoading} = useFetch<{item: RecordType}>('GET_ONE', 'Role', {variables: {id}});
  const [mutate] = useMutate<{item: RecordType}>('updateRolePermissions', '');

  const permissionResources = resourcesData?.getPermissionResources ?? [];

  const defaultGranted = useMemo(() => {
    const granted: Record<number, boolean> = {};
    const rolePermissions = roleData?.item?.permissions ?? [];

    rolePermissions.forEach((rolePermission: {granted?: boolean; permission?: {id?: number}}) => {
      if (rolePermission.permission?.id) {
        granted[rolePermission.permission.id] = !!rolePermission.granted;
      }
    });

    permissionResources.forEach((resource) => {
      resource.actions.forEach((action) => {
        if (action.permissionId && granted[action.permissionId] === undefined) {
          granted[action.permissionId] = false;
        }
      });
    });

    return granted;
  }, [permissionResources, roleData]);

  useEffect(() => {
    setGrantedByPermissionId(defaultGranted);
  }, [defaultGranted]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (typeof mutate !== 'function') {
      return;
    }

    const permissions = Object.entries(grantedByPermissionId).map(([permissionId, granted]) => ({
      permissionId: Number(permissionId),
      granted
    }));

    mutate({variables: {roleId: id, permissions}})
      .then((response) => {
        if (response.data?.item) {
          toast.success('Role permissions have been updated successfully!');
          if (typeof onSuccess === 'function') {
            onSuccess();
          }
          close();
        }
      })
      .catch((error) => {
        if (error.message) {
          toast.error(error.message);
        }
      });
  };

  const handleToggle = (permissionId: number) => (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setGrantedByPermissionId((current) => ({
      ...current,
      [permissionId]: checked
    }));
  };

  if (resourcesLoading || roleLoading) {
    return <Loader />;
  }

  const permissionFields = permissionResources.map((resource) => (
    <Box key={resource.resource}>
      <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1}}>
        {resource.description}
      </Typography>
      <Stack spacing={1}>
        {resource.actions.map((action) => {
          if (!action.permissionId) {
            return null;
          }

          return (
            <FormControlLabel
              key={action.key}
              control={
                <Switch
                  checked={!!grantedByPermissionId[action.permissionId]}
                  disabled={readOnly}
                  onChange={readOnly ? undefined : handleToggle(action.permissionId)}
                />
              }
              label={action.description}
            />
          );
        })}
      </Stack>
    </Box>
  ));

  if (readOnly) {
    return <Stack spacing={3}>{permissionFields}</Stack>;
  }

  return (
    <Stack component="form" id={ROLE_PERMISSIONS_FORM_ID} spacing={3} onSubmit={handleSubmit}>
      {permissionFields}
    </Stack>
  );
};

export default RolePermissionsForm;
